/** @jsxImportSource @emotion/react */
import moment from "moment";
import Typography from "@mui/material/Typography";

import { useRangeSelection } from "../../shared/hooks/useRangeSelection";
import {
  bubble,
  cell,
  monthGrid
} from "../../shared/styles/useDatePickerStyles";
import { useStripStyle } from "../hooks/useStripStyle";
import { MonthViewProps } from "../types";

const MonthView = ({
  year,
  months,
  endMonth,
  startMonth,
  hoverMonth,
  picking,
  maxDate,
  minDate,
  shouldDisableMonth,
  onMonthClick,
  cancelHoverClear,
  setHoverMonth
}: MonthViewProps) => {
  const { stripStyle } = useStripStyle();

  const now = new Date();

  const toMonthKey = (yearValue: number, monthIndex: number) =>
    yearValue * 12 + monthIndex;

  const todayKey = toMonthKey(now.getFullYear(), now.getMonth());

  const isMonthDisabled = (yearValue: number, monthIndex: number) => {
    const date = moment({
      year: yearValue,
      month: monthIndex,
      day: 1
    });

    const beforeMin =
      minDate && date.isBefore(minDate.clone().startOf("month"));

    const afterMax = maxDate && date.isAfter(maxDate.clone().startOf("month"));

    return Boolean(
      beforeMin || afterMax || shouldDisableMonth?.(date.toDate())
    );
  };

  const getMonthParts = (monthKey: number) => {
    const monthIndex = monthKey % 12;

    const yearValue = (monthKey - monthIndex) / 12;

    return {
      yearValue,
      monthIndex
    };
  };

  const isMonthKeyDisabled = (monthKey: number): boolean => {
    const { yearValue, monthIndex } = getMonthParts(monthKey);

    return isMonthDisabled(yearValue, monthIndex);
  };

  const { rangeStart, rangeEnd, isPreview, handleHover, getCellState } =
    useRangeSelection({
      startValue: startMonth,
      endValue: endMonth,
      hoverValue: hoverMonth,
      picking,
      isDisabled: isMonthKeyDisabled,
      setHoverValue: setHoverMonth,
      cancelHoverClear
    });

  const getCellProps = (monthIndex: number, monthKey: number) => {
    const state = getCellState({
      value: monthKey,
      todayValue: todayKey
    });

    const strip = stripStyle(monthKey, {
      rangeStart,
      rangeEnd,
      isPreview
    });

    return {
      strip,
      bubbleProps: {
        ...state,
        isDisabled: isMonthDisabled(year, monthIndex)
      }
    };
  };

  const isMonthBeyondMaxDate = (monthIndex: number) =>
    maxDate !== undefined &&
    new Date(year, monthIndex, 1) >=
      new Date(maxDate.year(), maxDate.month(), 1);

  return (
    <div
      css={monthGrid}
      onMouseEnter={cancelHoverClear}
      onMouseLeave={() => setHoverMonth(null)}
    >
      {months.map((month, monthIndex) => {
        if (isMonthBeyondMaxDate(monthIndex)) {
          return null;
        }

        const monthKey = toMonthKey(year, monthIndex);

        const { strip, bubbleProps } = getCellProps(monthIndex, monthKey);

        return (
          <div
            key={monthKey}
            css={cell}
            onMouseEnter={() => handleHover(monthKey)}
          >
            <div style={strip} />

            <Typography
              variant="body1Medium"
              css={bubble(bubbleProps)}
              onClick={() => onMonthClick(monthKey, year, monthIndex)}
            >
              {month}
            </Typography>
          </div>
        );
      })}
    </div>
  );
};

export default MonthView;
